import {
  getNextColorIndexForAppendedProposal,
  normalizeProposalsColorIndices,
} from '../../proposal.helpers';
import { createProposal } from '../../proposal-factory.helpers';
import { getUniqueProposalName } from '../../proposal-name.helpers';
import { getProposals } from '../../proposals/proposals.helpers';
import type { Baseline } from '@shared/models/project.model';
import {
  type IProposal,
  type ProposalId,
} from '@shared/models/proposals.model';

/**
 * Append a new {@link IProposal} to {@link Baseline.proposals}.
 *
 * Semantics:
 *  - assigns the next free automatic A/B/C name, or dedupes an explicit name
 *  - picks the next free palette index ({@link getNextColorIndexForAppendedProposal})
 *    without colliding with indices reserved for legacy grey rows (colorIndex 0)
 *  - keeps colour-index invariants by re-running {@link normalizeProposalsColorIndices}
 *  - always starts with an empty {@link IProposal.actionIds} list so a new row never
 *    shares {@link Baseline.actions} with an existing proposal (DEV-3268)
 *
 * Returns a version with the new proposal appended.
 */
export const addProposal = (
  version: Baseline,
  defaults: Partial<IProposal> = {},
  proposalId?: ProposalId,
): Baseline => {
  const proposals = getProposals(version);
  const name = getUniqueProposalName(
    proposals.map((p) => p.name),
    defaults.name,
  );

  const colorIndex =
    defaults.colorIndex ?? getNextColorIndexForAppendedProposal(proposals);

  const newProposal = createProposal(
    {
      ...defaults,
      ...(proposalId ? { id: proposalId } : {}),
      name,
      colorIndex,
      // Never inherit another proposal's action chain: shared action ids make
      // subtract/prune mutate one row both proposals reference (DEV-3268).
      actionIds: [],
    },
    proposalId ? false : undefined,
  );

  return {
    ...version,
    proposals: normalizeProposalsColorIndices([...proposals, newProposal]),
  };
};
